/**
 * Componente Medallero - Muestra el progreso del usuario
 * Diseño visual tipo vitrina de trofeos con especialidades, clases y certificaciones
 */

import {
  useMemo,
  useState,
  useRef,
  useLayoutEffect,
  type CSSProperties,
} from "react";
import {
  Card,
  CardContent,
  Badge,
  Button,
  SpecialtyImage,
} from "@/components/ui";
import {
  Award,
  Trophy,
  GraduationCap,
  Medal,
  Loader2,
  Star,
  Download,
  X,
  Sparkles,
  Clock,
} from "lucide-react";
import { useAuthStore } from "@/stores/authStore";
import { useMembersByLinkedUser } from "@/hooks/usePersons";
import {
  useAchievementsByPerson,
  useClassProgressByPerson,
} from "@/hooks/useProgress";
import { useSpecialties, useCertificaciones } from "@/hooks/useCatalogs";
import { useClub, useAssociation } from "@/hooks/useOrganization";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import type { Achievement, ClassProgress } from "@/types";

// Mapa de imágenes de clases
const classImageMap: Record<string, string> = {
  amigo: "/images/class/amigo.jpg",
  companero: "/images/class/companero.jpg",
  compañero: "/images/class/companero.jpg",
  explorador: "/images/class/explorador.jpg",
  orientador: "/images/class/orientador.jpg",
  viajero: "/images/class/viajero.jpg",
  guia: "/images/class/guia.jpg",
  guía: "/images/class/guia.jpg",
  "guia mayor": "/images/class/guia-mayor.jpg",
  "guía mayor": "/images/class/guia-mayor.jpg",
};

function getClassImage(className: string): string {
  const normalized = className
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return (
    classImageMap[normalized] ||
    classImageMap[className.toLowerCase()] ||
    "/images/class/amigo.jpg"
  );
}

interface CertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  achievement: Achievement | null;
  classProgress: ClassProgress | null;
  userName: string;
  imageUrl?: string;
  specialtyCategorySlug?: string;
  clubName?: string;
  associationName?: string;
}

function CertificateModal({
  isOpen,
  onClose,
  achievement,
  classProgress,
  userName,
  imageUrl,
  specialtyCategorySlug,
  clubName,
  associationName,
}: CertificateModalProps) {
  const certificateRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Certificado a tamaño de diseño FIJO (horizontal) escalado para caber en la
  // pantalla → se ve idéntico en móvil/desktop y la descarga queda en alta
  // resolución (html2canvas captura el ref a su tamaño real, no el escalado).
  const DESIGN_W = 760;
  const DESIGN_H = Math.round(DESIGN_W / 1.414);
  const [scale, setScale] = useState(1);
  // Mientras se captura el PNG, desactivamos las animaciones para que html2canvas
  // no congele un fotograma con elementos a opacidad 0 (gotcha conocido).
  const [capturing, setCapturing] = useState(false);
  useLayoutEffect(() => {
    if (!isOpen) return;
    const el = previewRef.current;
    if (!el) return;
    const compute = () => setScale(Math.min(1, el.clientWidth / DESIGN_W));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen]);

  if (!isOpen) return null;

  const itemName = achievement?.catalogName || classProgress?.className || "";
  const itemType = achievement?.type || "class";
  const earnedDate = achievement?.earnedDate || classProgress?.completedDate;

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, "d 'de' MMMM 'de' yyyy", { locale: es });
  };

  const getTypeLabel = () => {
    switch (itemType) {
      case "specialty":
        return "Especialidad";
      case "mastery":
        return "Maestría";
      case "certification":
        return "Certificación";
      case "class":
        return "Clase Progresiva";
      default:
        return "Logro";
    }
  };

  const getGradient = () => {
    switch (itemType) {
      case "specialty":
        return "from-purple-600 via-purple-500 to-indigo-600";
      case "mastery":
        return "from-amber-500 via-yellow-500 to-orange-500";
      case "certification":
        return "from-emerald-500 via-green-500 to-teal-500";
      default:
        return "from-blue-500 via-blue-600 to-indigo-600";
    }
  };

  const handleDownload = async () => {
    if (!certificateRef.current) return;
    setIsDownloading(true);
    setCapturing(true);
    // Esperar un par de frames para que React re-renderice sin animaciones.
    await new Promise((r) =>
      requestAnimationFrame(() => requestAnimationFrame(() => r(null))),
    );

    try {
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
      });

      const link = document.createElement("a");
      link.download = `certificado-${itemName.toLowerCase().replace(/\s+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Error generating certificate:", error);
    } finally {
      setCapturing(false);
      setIsDownloading(false);
    }
  };

  // Folio (serial) + instructor + verbo descriptivo del logro.
  const folio = (achievement?.id || classProgress?.id || "")
    .slice(0, 8)
    .toUpperCase();
  const instructorName = achievement?.instructorName;
  const itemTypeLower = (achievement?.type || "class") as string;
  const descripcion =
    itemTypeLower === "class"
      ? "por haber completado satisfactoriamente la clase progresiva"
      : itemTypeLower === "mastery"
        ? "por haber alcanzado con excelencia la maestría"
        : itemTypeLower === "certification"
          ? "por haber obtenido la certificación"
          : "por haber completado satisfactoriamente la especialidad";
  // Estilo de animación de entrada escalonada (desactivada al capturar el PNG).
  const anim = (delay: number): CSSProperties =>
    capturing ? {} : { animation: `certIn 0.6s ease ${delay}s both` };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      style={{ animation: "certFade .25s ease both" }}
    >
      <div
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[95vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "certPanel .3s ease both" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 p-3 sm:p-4 border-b bg-gradient-to-r from-gray-50 to-gray-100">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2 min-w-0">
            <Award className="w-5 h-5 text-purple-600 flex-shrink-0" />
            <span className="truncate">Certificado</span>
          </h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
              className="gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-all duration-300"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Descargar </span>PNG
            </Button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-200 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Certificate Preview — diseño fijo escalado para caber (responsive) */}
        <div className="p-3 sm:p-8 bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
          <div ref={previewRef} className="w-full">
            <div
              className="mx-auto"
              style={{ width: DESIGN_W * scale, height: DESIGN_H * scale }}
            >
              <div
                style={{
                  width: DESIGN_W,
                  height: DESIGN_H,
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }}
              >
                <div
                  ref={certificateRef}
                  className="relative bg-gradient-to-br from-amber-50 via-white to-amber-50 rounded-lg shadow-2xl overflow-hidden"
                  style={{ width: DESIGN_W, height: DESIGN_H }}
                >
            {/* Fondo decorativo con patrón */}
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            {/* Marco dorado exterior */}
            <div className="absolute inset-3 border-2 border-amber-400/40 rounded-lg" />
            <div className="absolute inset-5 border border-amber-300/30 rounded-lg" />

            {/* Esquinas decorativas doradas */}
            <div className="absolute top-6 left-6 w-12 h-12 border-t-[3px] border-l-[3px] border-amber-500/60 rounded-tl-lg" />
            <div className="absolute top-6 right-6 w-12 h-12 border-t-[3px] border-r-[3px] border-amber-500/60 rounded-tr-lg" />
            <div className="absolute bottom-6 left-6 w-12 h-12 border-b-[3px] border-l-[3px] border-amber-500/60 rounded-bl-lg" />
            <div className="absolute bottom-6 right-6 w-12 h-12 border-b-[3px] border-r-[3px] border-amber-500/60 rounded-br-lg" />

            {/* Keyframes de animación (no afectan la captura PNG: se desactivan) */}
            <style>{`
              @keyframes certIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
              @keyframes certSeal { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); } }
              @keyframes certFade { from { opacity: 0; } to { opacity: 1; } }
              @keyframes certPanel { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: none; } }
            `}</style>

            {/* Sparkles decorativos */}
            <Sparkles className="absolute top-9 right-9 w-4 h-4 text-amber-400/40" />
            <Sparkles className="absolute bottom-9 left-9 w-3.5 h-3.5 text-amber-400/30" />

            {/* Marca de agua: ícono Insignia */}
            <img
              src="/branding/insignia_icon.svg"
              aria-hidden
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 opacity-[0.04] pointer-events-none select-none"
            />

            {/* Contenido del certificado */}
            <div className="relative h-full flex flex-col px-16 py-7">
              {/* HEADER: logo Insignia + asociación */}
              <div className="text-center" style={anim(0.05)}>
                <img
                  src="/branding/insignia_logo.svg"
                  alt="Insignia"
                  className="h-7 w-auto mx-auto"
                />
                {associationName && (
                  <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-semibold mt-2.5">
                    {associationName}
                  </p>
                )}
                <div className="mt-2.5 flex items-center justify-center gap-2">
                  <div className="w-20 h-px bg-gradient-to-r from-transparent to-amber-400" />
                  <Star className="w-3 h-3 text-amber-500 fill-amber-400" />
                  <div className="w-20 h-px bg-gradient-to-l from-transparent to-amber-400" />
                </div>
              </div>

              {/* BODY */}
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div style={anim(0.12)}>
                  <p className="text-[12px] uppercase tracking-[0.3em] text-gray-400">
                    Certificado de
                  </p>
                  <p
                    className={`text-[26px] font-extrabold uppercase tracking-[0.12em] mt-0.5 ${
                      itemType === "mastery"
                        ? "text-amber-600"
                        : itemType === "certification"
                          ? "text-emerald-600"
                          : itemType === "class"
                            ? "text-blue-700"
                            : "text-indigo-700"
                    }`}
                  >
                    {getTypeLabel()}
                  </p>
                </div>

                {/* Insignia / imagen */}
                <div className="my-4" style={anim(0.2)}>
                  {itemType === "specialty" && specialtyCategorySlug ? (
                    <div className="w-24 h-24 rounded-2xl overflow-hidden shadow-xl ring-4 ring-amber-200/50 bg-white p-1">
                      <SpecialtyImage
                        name={itemName}
                        categorySlug={specialtyCategorySlug}
                        size="lg"
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </div>
                  ) : itemType === "class" ? (
                    <div className="w-24 h-24 rounded-full overflow-hidden shadow-xl ring-4 ring-blue-200/50 bg-white p-1">
                      <img
                        src={getClassImage(itemName)}
                        alt={itemName}
                        className="w-full h-full object-cover rounded-full"
                      />
                    </div>
                  ) : imageUrl ? (
                    <div className="w-24 h-24 flex items-center justify-center bg-white rounded-2xl shadow-xl ring-4 ring-emerald-200/50 p-2">
                      <img
                        src={imageUrl}
                        alt={itemName}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-24 h-24 rounded-full bg-gradient-to-br ${getGradient()} flex items-center justify-center shadow-xl ring-4 ring-white`}
                    >
                      {itemType === "mastery" ? (
                        <Trophy className="w-12 h-12 text-white" />
                      ) : (
                        <Medal className="w-12 h-12 text-white" />
                      )}
                    </div>
                  )}
                </div>

                <p
                  className="text-[11px] uppercase tracking-widest text-gray-400"
                  style={anim(0.28)}
                >
                  Se otorga a
                </p>
                <h2
                  className="text-3xl font-bold text-gray-800 leading-tight"
                  style={{ fontFamily: "Georgia, serif", ...anim(0.32) }}
                >
                  {userName}
                </h2>

                <p
                  className="text-[13px] text-gray-500 mt-2 max-w-md"
                  style={anim(0.38)}
                >
                  {descripcion}{" "}
                  <span className="font-semibold text-gray-700">{itemName}</span>
                </p>

                {(clubName || associationName) && (
                  <p
                    className="text-xs text-gray-600 mt-1.5 font-medium"
                    style={anim(0.44)}
                  >
                    {clubName ? `Club ${clubName}` : ""}
                    {clubName && associationName ? " · " : ""}
                    {associationName || ""}
                  </p>
                )}
                {instructorName && (
                  <p
                    className="text-[11px] text-gray-400 mt-0.5"
                    style={anim(0.48)}
                  >
                    Instructor: {instructorName}
                  </p>
                )}
              </div>

              {/* FOOTER: firmas + sello + meta */}
              <div
                className="flex items-end justify-between gap-4"
                style={anim(0.54)}
              >
                <div className="text-center flex-1 max-w-[150px]">
                  <div className="h-px bg-gray-400/60 mb-1" />
                  <p className="text-[10px] font-medium text-gray-600">
                    Director del Club
                  </p>
                </div>

                {/* Sello dorado */}
                <div
                  className="flex-shrink-0 -mb-1"
                  style={
                    capturing
                      ? {}
                      : { animation: "certSeal 2.6s ease-in-out infinite" }
                  }
                >
                  <div className="w-[62px] h-[62px] rounded-full bg-gradient-to-br from-amber-300 to-amber-500 p-[3px] shadow-lg ring-1 ring-amber-300/50">
                    <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                      <img
                        src="/branding/insignia_icon.svg"
                        alt="Insignia"
                        className="w-8 h-8"
                      />
                    </div>
                  </div>
                </div>

                <div className="text-center flex-1 max-w-[150px]">
                  <div className="h-px bg-gray-400/60 mb-1" />
                  <p className="text-[10px] font-medium text-gray-600">
                    Coordinación de Clubes
                  </p>
                </div>
              </div>

              {/* Meta: fecha · marca · folio */}
              <div className="flex items-center justify-between mt-3 text-[10px] text-gray-400">
                <span>Emitido el {earnedDate ? formatDate(earnedDate) : "—"}</span>
                <span className="tracking-wide">Certificado digital · Insignia</span>
                <span>Folio {folio || "—"}</span>
              </div>
                </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MyProgressCard() {
  const { profile } = useAuthStore();
  const { data: memberships, isLoading: loadingMemberships } =
    useMembersByLinkedUser(profile?.id);

  const [selectedAchievement, setSelectedAchievement] =
    useState<Achievement | null>(null);
  const [selectedClass, setSelectedClass] = useState<ClassProgress | null>(
    null,
  );
  const [selectedImageUrl, setSelectedImageUrl] = useState<
    string | undefined
  >();
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<
    string | undefined
  >();
  const [showCertificate, setShowCertificate] = useState(false);

  // Obtener todos los IDs únicos para buscar logros
  const allPersonIds = useMemo(() => {
    if (!memberships || memberships.length === 0) return [];
    const ids = new Set<string>();
    memberships.forEach((m) => {
      console.log("[WEB] MyProgressCard membership:", {
        id: m.id,
        personId: m.personId,
      });
      if (m.personId) ids.add(m.personId);
      if (m.id) ids.add(m.id);
    });
    console.log("[WEB] MyProgressCard allPersonIds:", Array.from(ids));
    return Array.from(ids);
  }, [memberships]);

  const personId = allPersonIds[0];
  console.log("[WEB] MyProgressCard using personId:", personId);

  const { data: achievements, isLoading: loadingAchievements } =
    useAchievementsByPerson(personId || "");
  const { data: classProgress, isLoading: loadingClasses } =
    useClassProgressByPerson(personId || "");
  const { data: specialties } = useSpecialties();
  const { data: certificaciones } = useCertificaciones();

  // Club + asociación del miembro (para enriquecer el certificado).
  const primaryMembership = memberships?.[0];
  const { data: club } = useClub(primaryMembership?.clubId || "");
  const { data: association } = useAssociation(
    club?.associationId || primaryMembership?.associationId || "",
  );

  console.log(
    "[WEB] MyProgressCard achievements:",
    achievements?.length,
    achievements,
  );
  console.log(
    "[WEB] MyProgressCard classProgress:",
    classProgress?.length,
    classProgress,
  );

  // Mapa de especialidades para imágenes
  const specialtyMap = useMemo(() => {
    const map = new Map<string, { categorySlug: string; name: string }>();
    specialties?.forEach((s) =>
      map.set(s.id, { categorySlug: s.categorySlug || "", name: s.name }),
    );
    return map;
  }, [specialties]);

  // Mapa de certificaciones para imágenes
  const certificacionMap = useMemo(() => {
    const map = new Map<string, string>();
    certificaciones?.forEach((c) => {
      if (c.localImage) {
        map.set(c.id, c.localImage);
      }
    });
    return map;
  }, [certificaciones]);

  // Agrupar logros por tipo
  const achievementsByType = useMemo(() => {
    const grouped: Record<string, Achievement[]> = {
      specialty: [],
      mastery: [],
      certification: [],
    };
    achievements
      ?.filter((a) => a.status === "valid")
      .forEach((a) => {
        if (grouped[a.type]) {
          grouped[a.type].push(a);
        }
      });
    return grouped;
  }, [achievements]);

  // Clases completadas y en progreso
  const completedClasses = useMemo(() => {
    return (
      classProgress?.filter(
        (c) =>
          c.status === "completed" ||
          c.status === "approved" ||
          c.status === "invested",
      ) || []
    );
  }, [classProgress]);

  const inProgressClasses = useMemo(() => {
    return classProgress?.filter((c) => c.status === "in_progress") || [];
  }, [classProgress]);

  // Estadísticas totales
  const totalAchievements =
    achievementsByType.specialty.length +
    achievementsByType.mastery.length +
    achievementsByType.certification.length +
    completedClasses.length;

  const isLoading = loadingMemberships || loadingAchievements || loadingClasses;

  const handleOpenCertificate = (
    achievement?: Achievement,
    classItem?: ClassProgress,
    imageUrl?: string,
    categorySlug?: string,
  ) => {
    setSelectedAchievement(achievement || null);
    setSelectedClass(classItem || null);
    setSelectedImageUrl(imageUrl);
    setSelectedCategorySlug(categorySlug);
    setShowCertificate(true);
  };

  if (isLoading) {
    return (
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg">
              <Trophy className="w-7 h-7 text-yellow-300" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Mi Medallero</h3>
              <p className="text-white/80 text-sm">
                Tus logros y reconocimientos
              </p>
            </div>
          </div>
        </div>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!memberships?.length) {
    return (
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg">
              <Trophy className="w-7 h-7 text-yellow-300" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Mi Medallero</h3>
              <p className="text-white/80 text-sm">
                Tus logros y reconocimientos
              </p>
            </div>
          </div>
        </div>
        <CardContent className="p-6">
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <Trophy className="w-10 h-10 text-gray-500" />
            </div>
            <p className="text-gray-500 font-medium">
              No estás registrado como miembro en ningún club
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Tu progreso aparecerá aquí cuando seas miembro de un club
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasProgress = totalAchievements > 0 || inProgressClasses.length > 0;

  return (
    <>
      <Card className="overflow-hidden border-0 shadow-lg">
        {/* Header con gradiente */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm shadow-lg">
                <Trophy className="w-7 h-7 text-yellow-300" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Mi Medallero</h3>
                <p className="text-white/80 text-sm">
                  Tus logros y reconocimientos
                </p>
              </div>
            </div>
            {hasProgress && (
              <div className="text-right">
                <p className="text-3xl font-bold">{totalAchievements}</p>
                <p className="text-white/80 text-sm">logros totales</p>
              </div>
            )}
          </div>
        </div>

        <CardContent className="p-6">
          {!hasProgress ? (
            <div className="text-center py-8">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                <Award className="w-12 h-12 text-gray-500" />
              </div>
              <p className="text-gray-600 font-medium">
                ¡Comienza tu aventura!
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Tus especialidades, clases y certificaciones aparecerán aquí
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Clases en Progreso */}
              {inProgressClasses.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-blue-500" />
                    <h4 className="font-semibold text-gray-900">
                      Clases en Progreso
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {inProgressClasses.map((cp) => (
                      <div
                        key={cp.id}
                        className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full overflow-hidden shadow-md border-2 border-blue-200">
                            <img
                              src={getClassImage(cp.className)}
                              alt={cp.className}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {cp.className}
                            </p>
                            <p className="text-xs text-gray-500">
                              {cp.completionPercentage}% completado
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-24 h-2.5 bg-blue-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                              style={{ width: `${cp.completionPercentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-blue-600">
                            {cp.completionPercentage}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clases Completadas - Estilo PIN/Insignia */}
              {completedClasses.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">
                      Clases Progresivas
                    </h4>
                    <Badge className="bg-blue-100 text-blue-700">
                      {completedClasses.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {completedClasses.map((cp) => (
                      <button
                        key={cp.id}
                        onClick={() => handleOpenCertificate(undefined, cp)}
                        className="group relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-200"
                      >
                        {/* Pin de clase con imagen */}
                        <div className="w-20 h-20 mx-auto mb-3 relative">
                          <div className="w-full h-full rounded-full overflow-hidden shadow-lg ring-4 ring-white group-hover:scale-110 transition-transform duration-200">
                            <img
                              src={getClassImage(cp.className)}
                              alt={cp.className}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {cp.status === "invested" && (
                            <div className="absolute -top-1 -right-1 w-7 h-7 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                              <Star className="w-4 h-4 text-white fill-white" />
                            </div>
                          )}
                        </div>
                        <p className="font-semibold text-gray-900 text-center text-sm">
                          {cp.className}
                        </p>
                        <p className="text-xs text-gray-500 text-center mt-1">
                          {cp.status === "invested"
                            ? "Investido"
                            : "Completado"}
                        </p>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-blue-600/0 group-hover:bg-blue-600/5 rounded-xl transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <Download className="w-5 h-5 text-blue-600" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Especialidades - Grid de insignias */}
              {achievementsByType.specialty.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Award className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">
                      Especialidades
                    </h4>
                    <Badge className="bg-purple-100 text-purple-700">
                      {achievementsByType.specialty.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {achievementsByType.specialty.map((achievement) => {
                      const specialty = specialtyMap.get(achievement.catalogId);
                      return (
                        <button
                          key={achievement.id}
                          onClick={() =>
                            handleOpenCertificate(
                              achievement,
                              undefined,
                              undefined,
                              specialty?.categorySlug,
                            )
                          }
                          className="group relative"
                          title={achievement.catalogName}
                        >
                          <div className="relative">
                            <SpecialtyImage
                              name={achievement.catalogName}
                              categorySlug={specialty?.categorySlug || ""}
                              size="lg"
                              className="w-full aspect-square rounded-xl border-4 border-purple-200 group-hover:border-purple-400 group-hover:shadow-xl group-hover:scale-105 transition-all duration-200"
                            />
                            {/* Shine effect on hover */}
                            <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-white/0 via-white/30 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                          </div>
                          <p className="text-xs text-gray-600 text-center mt-2 line-clamp-2 group-hover:text-purple-600 transition-colors duration-200">
                            {achievement.catalogName}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Maestrías - Trofeos dorados */}
              {achievementsByType.mastery.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <h4 className="font-semibold text-gray-900">Maestrías</h4>
                    <Badge className="bg-amber-100 text-amber-700">
                      {achievementsByType.mastery.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {achievementsByType.mastery.map((achievement) => (
                      <button
                        key={achievement.id}
                        onClick={() => handleOpenCertificate(achievement)}
                        className="group bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all duration-200"
                      >
                        <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                          <Trophy className="w-7 h-7 text-white" />
                        </div>
                        <p className="font-semibold text-gray-900 text-center text-sm">
                          {achievement.catalogName}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Certificaciones - Medallas */}
              {achievementsByType.certification.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Medal className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-semibold text-gray-900">
                      Certificaciones
                    </h4>
                    <Badge className="bg-emerald-100 text-emerald-700">
                      {achievementsByType.certification.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {achievementsByType.certification.map((achievement) => {
                      const certImage = certificacionMap.get(
                        achievement.catalogId,
                      );
                      return (
                        <button
                          key={achievement.id}
                          onClick={() =>
                            handleOpenCertificate(
                              achievement,
                              undefined,
                              certImage,
                            )
                          }
                          className="group bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border-2 border-emerald-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-200"
                        >
                          {/* Imagen de certificación */}
                          <div className="w-20 h-20 mx-auto mb-3 relative">
                            {certImage ? (
                              <img
                                src={certImage}
                                alt={achievement.catalogName}
                                className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-200"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-green-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200">
                                <Medal className="w-10 h-10 text-white" />
                              </div>
                            )}
                          </div>
                          <p className="font-semibold text-gray-900 text-center text-sm">
                            {achievement.catalogName}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Certificado */}
      <CertificateModal
        isOpen={showCertificate}
        onClose={() => setShowCertificate(false)}
        achievement={selectedAchievement}
        classProgress={selectedClass}
        userName={profile?.displayName || ""}
        imageUrl={selectedImageUrl}
        specialtyCategorySlug={selectedCategorySlug}
        clubName={club?.name}
        associationName={association?.name}
      />
    </>
  );
}
